// eslint-disable-next-line no-redeclare, @typescript-eslint/no-redeclare
import { Box, Center, Divider, Group, Paper, Skeleton, Stack, Text, Tooltip } from '@mantine/core';
import { useLingui } from '@lingui/react';
import { FC, MouseEvent, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconExternalLink } from '@tabler/icons';
import { useElementSize } from '@mantine/hooks';
import { WindowContext } from '../../Service/Context/WindowContext';
import { getMantineColor } from '../../Types/Enums/BookingState';
import { BookingEventDateItem } from '../../Types/Event';
import { BookingStateIcon } from '../../Atoms/StateIcon';
import { useAppUser } from '../../Service/Context/ApplicationUserContext';
import { RoutingS } from '../../Service/RoutingS';
import { SpanDot } from '../../Atoms/Dot';
import { Contact, ContactS } from '../../Service/ContactS';
import { EventsContext } from '../../Service/Context/EventsContext';
import { isDefined } from '../../Service/ValidationS';
import { useJwtClaims } from '../../Service/Context/AuthContext';
import LocationDescription from './LocationDescription';
import EventRecommendationListWrapper from './EventRecommendations/EventRecommendationListWrapper';

const EventDateItemMultiple: FC<{ bookingEventDates: BookingEventDateItem[] }> = ({ bookingEventDates }) => {
  const { licence } = useJwtClaims();
  const { i18n } = useLingui();
  const { query, filter } = useContext(EventsContext) ?? {};
  const { sorting } = filter ?? { sorting: 'chronological' };
  const { redGreenWeakness } = useAppUser();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [customer, setCustomer] = useState<Contact>();
  const { xs, sm, md } = useContext(WindowContext);
  const { ref, width } = useElementSize();
  const primaryEventDate = bookingEventDates.find((ed) => ed.useAsPrimary);
  const { eventId, bookingState, recommendations, customerId, eventState, start, weekday, eventName, kind, kindColor } =
    bookingEventDates[0];
  const eventDate = new Date(start);
  const eventDay = eventDate.getDate();
  const customerMissing = !loading && !customer;
  const color =
    eventState && licence && licence.includes('INTERACTIVE') ? eventState.color : getMantineColor(bookingState);
  const stateEmoji = eventState && licence && licence.includes('INTERACTIVE') ? eventState.emoji : null;
  const eventStateLabel = eventState ? eventState.label : i18n._(`event.state.${bookingState.toLocaleLowerCase()}`);
  const hideTitle =
    sorting !== 'createdAt' && isDefined(query) && customer && !ContactS.matchQuery(customer, query as string);

  useEffect(() => {
    setLoading(true);
    const abortController = new AbortController();
    ContactS.fetchById(customerId)
      .then((resJSON) => {
        if (resJSON) {
          setCustomer(resJSON);
        }
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });
    return () => abortController.abort();
  }, [customerId]);

  if (hideTitle) {
    return null;
  }

  const getCustomerDescription = () => {
    if (loading) {
      return <Skeleton height={25} width={250} />;
    }
    if (customer) {
      return `${eventName ? `${eventName} - ` : ''}${ContactS.getDescription(customer)}`;
    }
    return i18n._('errors.contact.not.synced');
  };

  const openInNewTab = (e: MouseEvent<SVGElement>) => {
    e.stopPropagation(); // Prevent bookitup event page to be opened
    RoutingS.openInNewTab(`${process.env.REACT_APP_FRONTEND_URL}/events/${eventId}/details`);
  };

  return (
    <Stack ref={ref} spacing={5} pos="relative">
      <Paper
        className="event-list-item"
        shadow="xs"
        radius={10}
        p="md"
        onClick={() => navigate(`/events/${eventId}/details`)}
        sx={(theme) => ({
          cursor: customerMissing ? 'not-allowed' : 'pointer',
          opacity: customerMissing ? 0.5 : 1,
          '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.colors.lightBlue[0],
          },
        })}
      >
        <Box sx={{ display: 'flex', minHeight: 70, position: 'relative', gap: 15 }}>
          {(redGreenWeakness || stateEmoji) && (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {stateEmoji && (
                <Tooltip withArrow position="left" transition="slide-left" label={eventStateLabel}>
                  <Text size="lg">{stateEmoji}</Text>
                </Tooltip>
              )}
              {!stateEmoji && redGreenWeakness && <BookingStateIcon bookingState={bookingState} size={16} />}
            </div>
          )}
          <Divider orientation="vertical" size="xl" color={color} sx={{ borderRadius: 10 }} />
          <div
            style={{
              display: 'flex',
              flexDirection: sm ? 'column' : 'row',
              alignItems: 'center',
              gap: 10,
              justifyContent: md ? 'center' : undefined,
            }}
          >
            <Text size="xs" transform="uppercase" weight="lighter">
              {weekday}
            </Text>
            <Text size={30} weight="bold">{`${eventDay < 10 ? '0' : ''}${eventDay}`}</Text>
            <Text size="xs" weight="lighter">{`${i18n
              ._(`month.${eventDate.getMonth() + 1}`)
              .toUpperCase()
              .substring(0, 3)}`}</Text>
          </div>
          <Divider orientation="vertical" />
          <Stack spacing={0}>
            <Text size="xs" color="dimmed" className="overflow-ellipsis" sx={{ maxWidth: width - 200 }}>
              {kind && (
                <>
                  {kindColor && <SpanDot withoutShadow colour={kindColor} />}
                  <b style={{ marginRight: 5 }}>{kind}</b>
                </>
              )}
            </Text>
            <Text
              my={5}
              size="md"
              weight="bolder"
              className="overflow-ellipsis"
              sx={{ maxWidth: width - (xs ? 200 : 250) }}
            >
              {getCustomerDescription()}
            </Text>
            {md && primaryEventDate && (
              <>
                {primaryEventDate.timeFrom && primaryEventDate.timeTo && (
                  <Text size="xs" weight="lighter" color="dimmed">
                    {`${primaryEventDate.timeFrom} bis ${primaryEventDate.timeTo}`}
                  </Text>
                )}
                {primaryEventDate.dateDescription && (
                  <Text
                    size="xs"
                    weight="lighter"
                    color="dimmed"
                    className="overflow-ellipsis"
                    sx={{ maxWidth: width - 200 }}
                  >
                    {primaryEventDate.dateDescription}
                  </Text>
                )}
                {primaryEventDate.locationId && (
                  <LocationDescription includeIcon={false} locationId={primaryEventDate.locationId} />
                )}
                <Text mt={5} size="xs" weight="lighter" color="dimmed">{`+ ${
                  bookingEventDates.length - 1
                } weitere`}</Text>
              </>
            )}
            {!md &&
              bookingEventDates.map((ed) => {
                const { timeFrom, timeTo, locationId, dateDescription } = ed;
                return (
                  <Group key={ed.id} spacing="xs">
                    {timeFrom && timeTo && (
                      <Text span size="xs" weight="lighter" color="dimmed">
                        {`${timeFrom} bis ${timeTo}`}
                      </Text>
                    )}
                    {dateDescription && (
                      <Text
                        color="dimmed"
                        size="xs"
                        weight="lighter"
                        className="overflow-ellipsis"
                        sx={{ maxWidth: width - 200 }}
                      >
                        {dateDescription}
                      </Text>
                    )}
                    {locationId && <LocationDescription includeIcon={false} locationId={locationId} />}
                  </Group>
                );
              })}
          </Stack>
          <Center>
            <Tooltip withArrow position="left" transition="slide-left" label="Im neuen Tab öffnen">
              <Box sx={{ position: 'absolute', right: 0 }}>
                <IconExternalLink onClick={openInNewTab} />
              </Box>
            </Tooltip>
          </Center>
        </Box>
      </Paper>
      <EventRecommendationListWrapper recommendations={recommendations} profileBookingState={bookingState} />
    </Stack>
  );
};

export default EventDateItemMultiple;
